// src/pages/About/About.jsx
import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import i18n from "@/i18n";
import cls from "./About.module.scss";
import JumpMenu from "./scenes/JumpMenu";
import { ORDER } from "./data/map";

async function loadScript(locale) {
  try {
    const mod = await import(`./data/scenes.${locale}.json`);
    return mod.default || mod;
  } catch {
    const mod = await import(`./data/scenes.en.json`);
    return mod.default || mod;
  }
}

export default function About() {
  const [script, setScript] = useState(null);
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(() => {
    const fromLS = localStorage.getItem("whoWeAreProgress");
    return fromLS ? JSON.parse(fromLS) : {};
  });

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const locale = i18n.language || "en";

  useEffect(() => {
    let alive = true;
    (async () => {
      const data = await loadScript(locale);
      if (!alive) return;
      setScript(data);
    })();
    return () => (alive = false);
  }, [locale]);

  useEffect(() => {
    localStorage.setItem("whoWeAreProgress", JSON.stringify(seen));
  }, [seen]);

  useEffect(() => {
    if (pathname === "/about-us") {
      const last = ORDER.find((id) => seen[id]);
      if (last) navigate(`/about-us/${last}`, { replace: true });
    }
  }, [pathname]); // eslint-disable-line

  // global event to open from center button
  useEffect(() => {
    const onToggle = (e) => setOpen((p) => (e.detail === true ? true : !p));
    window.addEventListener("about:toggleJump", onToggle);
    return () => window.removeEventListener("about:toggleJump", onToggle);
  }, []);

  // lock scroll when open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const ctx = useMemo(
    () => ({
      script,
      seen,
      markSeen: (id) => setSeen((s) => ({ ...s, [id]: true })),
      order: ORDER,
    }),
    [script, seen],
  );

  return (
    <section className={`${cls.page} nu-bg--desktop`} data-locale={locale}>
      <div className={cls.stage}>
        <Outlet context={ctx} />
      </div>

      {/* Backdrop */}
      <div
        className={cls.backdrop}
        hidden={!open}
        onClick={() => setOpen(false)}
      />

      {/* Drawer */}
      <aside
        id="about-chapters-drawer"
        className={cls.drawer}
        data-open={open ? "true" : "false"}
        role="dialog"
        aria-modal="true"
        aria-label="Chapters"
      >
        <header>
          <h3 className="nu-title">{i18n.t("about.chapters")}</h3>
        </header>

        <div className={cls.drawerList}>
          <JumpMenu
            script={script}
            seen={seen}
            order={ORDER}
            onSelect={() => setOpen(false)}
          />
        </div>
      </aside>
    </section>
  );
}
